/**
 * Lectura de un extracto en el dispositivo: CSV como texto (UTF-8 o, si no lo es, Windows-1252) y XLS/XLSX con
 * SheetJS (cargado solo cuando hace falta). Devuelve filas 2D; las fechas de Excel salen como 'aaaa-mm-dd'.
 */
import { parseCsvText, type ImportAdapter } from '@pf/domain';

export const MAX_FILE_BYTES = 10 * 1024 * 1024;

/**
 * Fecha de SheetJS → 'aaaa-mm-dd' sin desfases de zona horaria: tanto si viene como medianoche local como UTC,
 * sumando 12 horas cae en el mismo día UTC (en cualquier zona entre −12 y +12).
 */
function dateCell(d: Date): string {
  const t = new Date(d.getTime() + 12 * 3600 * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${t.getUTCFullYear()}-${p(t.getUTCMonth() + 1)}-${p(t.getUTCDate())}`;
}

export async function readStatement(file: File, ad: ImportAdapter): Promise<unknown[][]> {
  if (file.size > MAX_FILE_BYTES) throw new Error('El fichero es demasiado grande (máximo 10 MB)');
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  if (!ad.fileTypes.includes(ext)) throw new Error(`Para ${ad.label} elige un fichero ${ad.fileTypes.map(x => `.${x}`).join(' o ')}`);
  const buf = await file.arrayBuffer();
  if (ext === 'csv') {
    let text = new TextDecoder('utf-8').decode(buf);
    if (text.includes('�')) text = new TextDecoder('windows-1252').decode(buf);
    return parseCsvText(text, ad.csvDelimiter ?? ',');
  }
  const XLSX = await import('xlsx');
  const wb = XLSX.read(new Uint8Array(buf), { type: 'array', cellDates: true });
  const name = ad.sheetName && wb.SheetNames.includes(ad.sheetName) ? ad.sheetName : wb.SheetNames[0];
  if (!name) throw new Error('El fichero no tiene hojas');
  const rows = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[name]!, { header: 1, raw: true, defval: '' });
  return rows.map(r => r.map(v => (v instanceof Date ? dateCell(v) : v)));
}
